/* procedure utility functions file */
import {v4} from 'uuid';

import {getBasicTeamsFromRelated} from 'app/teams/utils';
import {gt} from 'app/utils/i18n';

import type {
  AnyStepType,
  AnyTaskStepType,
  ListResponseType,
  ProcedureAutomationStepType,
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  ProcedureEndingStepType,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
  ProcedureStepType,
  ProcedureVersionSerializedType,
  RawProcedureHashidSerializedType,
  RawProcedureSerializedType,
  RawProcedureVersionSerializedType,
  RelatedTeamsSerializedType,
} from 'sweet-types';

export function getDescription(procedure: {
  description: string | Record<string, unknown>;
}) {
  if (typeof procedure.description === 'string') {
    try {
      const description = JSON.parse(procedure.description);
      return validateDescription(description);
    } catch (e) {
      return validateDescription({
        content: procedure.description,
      });
    }
  } else {
    return validateDescription(procedure.description);
  }
}

export const validateDescription = (description: Record<string, unknown>) => ({
  content: '',
  images: [],
  embeds: [],
  files: [],
  ...description,
});

export const validateStep = (step: any, n?: string | number) => ({
  /* ensures the step object have all required keys and the right type */
  n,
  content: '',
  images: [],
  files: [],
  embeds: [],
  id: step.id != null ? step.id : generateStepId(),
  ...step,
});

export const getContent = (content: string): ProcedureStepType[] => {
  let steps: ProcedureStepType[];

  try {
    steps = JSON.parse(content);
  } catch (e) {
    steps = [];
  }

  return normalizeSteps(steps);
};

export function normalizeSteps<T extends AnyStepType | AnyTaskStepType>(
  steps: T[]
): T[] {
  return removeOrphanChoices(steps).map(validateStep);
}

export const removeOrphanChoices = <T extends AnyStepType | AnyTaskStepType>(
  steps: T[]
): T[] => {
  const stepsIds = steps.map((step) => step.id);

  return steps.map<T>((step) => {
    const newStep: T = {...step};

    if (newStep.type === 'decision' && newStep.choices != null) {
      newStep.choices = newStep.choices.filter(
        (choice) => stepsIds.indexOf(choice.dest) > -1
      );

      if (newStep.choices.length === 0) {
        newStep.choices = [];
      }
    }

    return newStep;
  });
};

export const generateStepId = () => v4().replace(/-/g, '');

export const generateNewContentStep = (
  title = '',
  content = {}
): ProcedureContentStepType => ({
  content: '',
  ...content,
  title,
  id: generateStepId(),
  // n value will be auto-set by updateStepsFunc
  n: 0,

  images: [],
  files: [],
  embeds: [],
  type: 'content',
});

export const generateNewDecisionStep = (
  title = '',
  content = {}
): ProcedureDecisionStepType => ({
  content: '',
  ...content,
  title,
  id: generateStepId(),
  // n value will be auto-set by updateStepsFunc
  n: 0,

  choices: [],
  type: 'decision',
});

export const generateNewAutomationStep = (
  title = '',
  content = {}
): ProcedureAutomationStepType => ({
  content: '',
  ...content,
  title,
  id: generateStepId(),
  // n value will be auto-set by updateStepsFunc
  n: 0,

  automation: {
    type: 'email',
    send_to: [],
    reply_to: [],
    cc: [],
    content: '',
    subject: '',
  },
  type: 'automation',
});

export const generateNewEndStep = (
  title = '',
  content = {}
): ProcedureEndingStepType => ({
  content: '',
  ...content,
  title,
  id: generateStepId(),
  // n value will be auto-set by updateStepsFunc
  n: 0,

  type: 'ending',
});

export const generateNewStep = (
  type: 'content' | 'decision' | 'automation' | 'ending',
  title = '',
  content = {}
) => {
  if (type === 'content') {
    return generateNewContentStep(title, content);
  }
  if (type === 'decision') {
    return generateNewDecisionStep(title, content);
  }
  if (type === 'automation') {
    return generateNewAutomationStep(title, content);
  }

  return generateNewEndStep(title, content);
};

export const hasFinalEnding = <I extends AnyStepType>(
  steps: Array<I>
): boolean => {
  const lastStep: I | null | undefined = steps[steps.length - 1];
  return lastStep != null && lastStep.type === 'ending';
};

export const ARTIFICIAL_ENDING_ID = 'artificial-ending';

export const getArtificialEndingStep = (
  n: number | null
): ProcedureEndingStepType => ({
  id: ARTIFICIAL_ENDING_ID,
  content: '',
  type: 'ending',
  n: n ?? -1,
  title: gt('End'),
});

/* efficiently check if the procedure structure (links between steps) was changed */
export function procedureStructureIsEqual(
  stepsA: Array<ProcedureStepType>,
  stepsB: Array<ProcedureStepType>
): boolean {
  if (stepsA.length !== stepsB.length) {
    return false;
  }

  for (let i = 0; i < stepsA.length; i++) {
    const stepA = stepsA[i];
    const stepB = stepsB[i];

    if (
      stepA.type !== stepB.type ||
      stepA.id !== stepB.id ||
      (stepA as any).next !== (stepB as any).next
    ) {
      return false;
    }

    if ((stepA as any).choices != null || (stepB as any).choices != null) {
      const stepAchoices = (stepA as any).choices || [];
      const stepBchoices = (stepB as any).choices || [];

      if (stepAchoices.length !== stepBchoices.length) {
        return false;
      }

      for (let j = 0; j < stepAchoices.length; j++) {
        const choiceA = stepAchoices[j];
        const choiceB = stepBchoices[j];

        if (choiceA.id !== choiceB.id || choiceA.dest !== choiceB.dest) {
          return false;
        }
      }
    }
  }

  return true;
}

export function standardizeRawProcedure(
  doc: RawProcedureHashidSerializedType,
  related?: RelatedTeamsSerializedType
): ProcedureHashidSerializedType;
export function standardizeRawProcedure(
  doc: RawProcedureSerializedType,
  related?: RelatedTeamsSerializedType
): ProcedureSerializedType;
export function standardizeRawProcedure(
  doc: RawProcedureHashidSerializedType | RawProcedureSerializedType,
  related: RelatedTeamsSerializedType
): ProcedureHashidSerializedType | ProcedureSerializedType {
  return {
    ...doc,
    content: getContent(doc.content),
    description: getDescription(doc),
    team_names: getBasicTeamsFromRelated(doc, related),
  };
}

export function standardizeRawProcedureVersion(
  doc: RawProcedureVersionSerializedType
): ProcedureVersionSerializedType {
  return {
    ...doc,
    content: getContent(doc.content),
    description: getDescription(doc),
  };
}

export function standardizeRawProcedureVersions(
  doc: ListResponseType<RawProcedureVersionSerializedType>
): ListResponseType<ProcedureVersionSerializedType> {
  return {
    ...doc,
    results: doc.results.map(standardizeRawProcedureVersion),
  };
}

export const getDefaultAnswers = () => [
  {
    id: generateStepId(),
    text: '',
    correct: true,
  },
  {
    id: generateStepId(),
    text: '',
    correct: false,
  },
];
